import type {
  BrochureBlockInput, BrochureFlyerPick, BrochureFlyerPicks, BrochureGenerateInput, BrochurePhotoInput,
  BrochureSectionInput, FlyerSlot,
} from '../types';

/**
 * ⭐ THE BROCHURE EDITOR'S MODEL, IN BOTH DIRECTIONS.
 *
 * `toInput` is what the editor has always done: its text-shaped blocks ("Label: Value" per line,
 * one bullet per line, ...) become the builder's `BrochureGenerateInput`. `fromInput` is LB4
 * (his ruling 2026-09-09, "load a saved brochure"): a saved input comes back into the editor's
 * shape so it can be changed and generated again.
 *
 * ⭐ NOTHING IS DROPPED ON THE WAY IN. What the editor has no control for (the page size, the
 * cover's locality line, an OM's financials, the contact page's extra lines, a table's source
 * note, a facts card's lead, and a page block's OWN photographs when they differ from the grid)
 * is CARRIED through untouched and merged back by `toInput`, and `fromInput` NAMES every carried
 * thing so the screen can say "not editable here, kept as saved". A load that silently lost the
 * financials would print a Brochure where an Offering Memorandum was saved.
 *
 * The 2255 lease brochure (`backend/.../__fixtures__/floridaLeaseBrochure.ts`) is the round-trip
 * fixture: `toInput(fromInput(x).state)` must equal `x`.
 */
export type BlockKind = BrochureBlockInput['kind'];

export interface EditorBlock {
  id: number;
  kind: BlockKind;
  title: string;
  body: string;
  column: '' | '1' | '2';
  full: boolean;
  panel: boolean;
  /**
   * A loaded block whose fact labels carry no colon ("LEASE RATE", his 2255 page) keeps that
   * style: the editor shows "LEASE RATE: $0.85" so the line parses, and `toInput` takes the
   * colon back off. Without this a load would print a colon on every label of his page.
   */
  bareLabels?: boolean;
  /** An explicit `span: 'half'` on the saved block (the renderer reads it), carried as such. */
  spanHalf?: boolean;
  /** This block's own photographs, when a loaded brochure's differ from the editor's one grid. */
  photos?: { src: string; alt?: string }[];
  /** Fields of the saved block the editor has no control for, merged back untouched. */
  carried?: { lead?: string | null; sourceNote?: string | null };
}

export interface EditorSection {
  id: number;
  title: string;
  subtitle: string;
  sourceNote: string;
  omitDisclaimer: boolean;
  /** Three columns where his page has three - 2255's summary and overview pages. */
  columns: '2' | '3';
  /** No running footer on this page - his map and disclaimer pages carry none. */
  omitFooter: boolean;
  blocks: EditorBlock[];
}

/** What the editor has no control for, carried through a load so the brochure regenerates whole. */
export interface CarriedInput {
  pageSize?: BrochureGenerateInput['pageSize'];
  coverLocality?: string | null;
  /** The alt text of the cover and back-cover photographs - the editor holds their src alone. */
  coverPhotoAlt?: string | null;
  backPhotoAlt?: string | null;
  financials?: BrochureGenerateInput['financials'];
  contactQrLead?: string | null;
  contactClosing?: string | null;
  contactFooter?: string | null;
  flyerTitle?: string | null;
  flyerEyebrow?: string | null;
  flyerEyebrowFront?: string | null;
  /** The flyer's own two photographs when they are not the first two of the grid. */
  flyerPhotos?: BrochurePhotoInput[] | null;
}

export interface EditorState {
  addressLine: string; city: string; state: string; zip: string; county: string;
  dealKind: 'sale' | 'lease';
  format: 'brochure' | 'flyer';
  theme: 'apex' | 'manchester' | 'classic';
  flyerSummary: string;
  flyerCaptions: string;
  /** FL2 - the cards he named for the flyer's slots; a slot absent here is picked by title. */
  flyerPicks: Partial<Record<FlyerSlot, BrochureFlyerPick>>;
  bandSet: 'house' | 'leasing';
  coverStyle: 'card' | 'overlay' | 'band';
  tagline: string; headline: string; subhead: string; emphasis: string; coverStats: string; byline: string;
  brokerTitles: boolean;
  pageFooter: boolean;
  footerTag: string;
  omitBack: boolean;
  contactLead: string; contactNote: string; disclaimerText: string; backBand: string;
  includeRobert: boolean; includeJoseph: boolean;
  sections: EditorSection[];
  coverPhoto: string | null;
  backPhoto: string | null;
  gridPhotos: { src: string; alt?: string }[];
  planPhoto: string | null;
  qrSrc: string | null;
  carried: CarriedInput;
}

export const FLYER_SLOTS: FlyerSlot[] = ['specs', 'highlights', 'drive', 'demographics', 'uses'];
/** What each flyer slot is, and which card kinds can fill it - the editor's pickers offer only those. */
export const FLYER_SLOT_INFO: Record<FlyerSlot, { label: string; kinds: BlockKind[] }> = {
  specs: { label: 'Spec list (front)', kinds: ['facts'] },
  highlights: { label: 'Highlights (front)', kinds: ['bullets'] },
  drive: { label: 'Drive times (back)', kinds: ['facts'] },
  demographics: { label: 'Demographics (back)', kinds: ['table', 'statCards'] },
  uses: { label: 'Ideal users (back)', kinds: ['bullets'] },
};
/** The cards a slot may take, as picker options: the page title and the card title (or its kind when untitled). */
export const flyerPickOptions = (sections: EditorSection[], slot: FlyerSlot): { value: string; label: string; pick: BrochureFlyerPick }[] => {
  const out: { value: string; label: string; pick: BrochureFlyerPick }[] = [];
  for (const sec of sections) {
    if (!sec.title.trim()) continue;
    for (const b of sec.blocks) {
      if (!FLYER_SLOT_INFO[slot].kinds.includes(b.kind)) continue;
      if (b.kind !== 'statCards' && !b.title.trim()) continue;
      const pick = { section: sec.title.trim(), block: b.kind === 'statCards' ? '' : b.title.trim() };
      out.push({ value: JSON.stringify(pick), label: `${pick.section} · ${pick.block || '(stat cards)'}`, pick });
    }
  }
  return out;
};

export const lines = (s: string): string[] => s.split('\n').map((l) => l.trim()).filter(Boolean);

/** "Price: $279,000" -> { label: "Price:", value: "$279,000" }. The colon stays on the label,
 *  because his own labels are typed with it and the renderer adds none. */
export const splitPair = (line: string): { label: string; value: string } => {
  const i = line.indexOf(':');
  if (i < 0) return { label: line.trim(), value: '' };
  return { label: line.slice(0, i + 1).trim(), value: line.slice(i + 1).trim() };
};

export const parseStats = (body: string): { value: string; label: string }[] =>
  lines(body).map((l) => {
    const [value, label] = l.split('|').map((x) => x.trim());
    return { value: value ?? '', label: label ?? '' };
  }).filter((s) => s.value || s.label);

export const statsToText = (stats: { value: string; label: string }[] | null | undefined): string =>
  (stats ?? []).map((s) => `${s.value} | ${s.label}`).join('\n');

/** A ready-made stat-cards block rendered back into the editor's own text format, so the
 *  computed figures arrive editable rather than locked. */
export const statCardsToText = (block: BrochureBlockInput): string => {
  if (block.kind !== 'statCards') return '';
  return block.cards
    .map((c) => [`## ${c.title}`, ...c.stats.map((s) => `${s.value} | ${s.label}`)].join('\n'))
    .join('\n');
};

let nextId = 1;
export const newBlock = (kind: BlockKind = 'facts'): EditorBlock => ({
  id: nextId++, kind, title: '', body: '', column: '', full: false, panel: false,
});
export const newSection = (title = ''): EditorSection => ({
  id: nextId++, title, subtitle: '', sourceNote: '', omitDisclaimer: false,
  columns: '2', omitFooter: false, blocks: [newBlock()],
});

const samePhotos = (a: { src: string }[], b: { src: string }[]): boolean =>
  a.length === b.length && a.every((p, i) => p.src === b[i].src);

// ------------------------------------------------------------------------------------------
// Editor -> input
// ------------------------------------------------------------------------------------------

export const toBlock = (b: EditorBlock, photos: { src: string; alt?: string }[]): BrochureBlockInput | null => {
  const span = b.full ? ('full' as const) : b.spanHalf ? ('half' as const) : undefined;
  const column = b.column === '1' ? 1 : b.column === '2' ? 2 : undefined;
  const common = { span, column } as const;
  const bare = (label: string): string => (b.bareLabels ? label.replace(/:$/, '') : label);

  switch (b.kind) {
    case 'facts':
      return {
        kind: 'facts', title: b.title, panel: b.panel, ...common,
        rows: lines(b.body).map(splitPair).map((r) => ({ label: bare(r.label), value: r.value })),
        ...(b.carried?.lead != null ? { lead: b.carried.lead } : {}),
      };
    case 'bullets':
      return { kind: 'bullets', title: b.title, items: lines(b.body), panel: b.panel, ...common };
    case 'prose':
      return {
        kind: 'prose', title: b.title, panel: b.panel, ...common,
        paragraphs: b.body.split(/\n\s*\n/).map((p) => p.trim().replace(/\s*\n\s*/g, ' ')).filter(Boolean),
      };
    case 'groups': {
      const all = lines(b.body);
      // "columns: 3" as the first line lays the groups out as a grid of cells.
      const colLine = all[0]?.match(/^columns:\s*([23])$/i);
      const columns = colLine ? (Number(colLine[1]) as 2 | 3) : undefined;
      return {
        kind: 'groups', title: b.title, panel: b.panel, ...common, columns,
        groups: (colLine ? all.slice(1) : all).map((l) => {
          const { label, value } = splitPair(l);
          return { label: label.replace(/:$/, ''), text: value };
        }),
      };
    }
    case 'statRow': {
      const all = lines(b.body);
      const noteLine = all.find((l) => /^note:/i.test(l));
      const stats = all.filter((l) => !/^note:/i.test(l)).map((l) => {
        const [value, label] = l.split('|').map((x) => x.trim());
        return { value: value ?? '', label: label ?? '' };
      }).filter((s) => s.value || s.label);
      if (!stats.length) return null;
      return {
        kind: 'statRow', title: b.title.trim() || null, stats,
        note: noteLine ? noteLine.replace(/^note:\s*/i, '') : null, ...common,
      };
    }
    case 'callout': {
      const all = lines(b.body);
      // A first line that is short and money-ish reads as the big figure, exactly as his
      // PRICED TO MOVE box does. Anything else is all body - nothing is invented either way.
      const figure = all.length > 1 && all[0].length <= 24 ? all[0] : null;
      const body = (figure ? all.slice(1) : all).join(' ');
      return { kind: 'callout', title: b.title, body, figure, ...common };
    }
    case 'statBand': {
      const all = lines(b.body);
      if (all.length < 2) return null;
      return {
        kind: 'statBand', label: all[0], figure: all[1],
        columns: all.slice(2).map(splitPair).map((c) => ({ label: c.label.replace(/:$/, ''), value: c.value })),
      };
    }
    case 'statCards': {
      const cards: { title: string; stats: { value: string; label: string }[] }[] = [];
      for (const l of lines(b.body)) {
        if (l.startsWith('##')) { cards.push({ title: l.replace(/^##\s*/, ''), stats: [] }); continue; }
        const [value, label] = l.split('|').map((x) => x.trim());
        if (cards.length && value && label) cards[cards.length - 1].stats.push({ value, label });
      }
      return cards.length ? { kind: 'statCards', cards } : null;
    }
    case 'table': {
      const all = lines(b.body);
      if (all.length < 2) return null;
      const columns = all[0].split('|').map((x) => x.trim());
      const rest = all.slice(1);
      const totalsLine = rest.find((l) => l.startsWith('='));
      return {
        kind: 'table', title: b.title, columns,
        rows: rest.filter((l) => !l.startsWith('=')).map((l) => l.split('|').map((x) => x.trim())),
        totals: totalsLine ? totalsLine.replace(/^=\s*/, '').split('|').map((x) => x.trim()) : null,
        ...common,
        ...(b.carried?.sourceNote != null ? { sourceNote: b.carried.sourceNote } : {}),
      };
    }
    case 'photos': {
      // "height: 300" and "columns: 4" lines size the grid; the rest are captions - one per
      // line, in photo order, captions each photograph (both 2026 specimens); any other count
      // of lines is one caption for the whole grid (San Jacinto).
      const own = b.photos ?? photos;
      const all = lines(b.body);
      let height: number | undefined;
      let columns: 2 | 3 | 4 | undefined;
      const caps = all.filter((l) => {
        const h = l.match(/^height:\s*(\d+)$/i);
        const c = l.match(/^columns:\s*([234])$/i);
        if (h) height = Number(h[1]);
        if (c) columns = Number(c[1]) as 2 | 3 | 4;
        return !h && !c;
      });
      const perPhoto = own.length > 0 && caps.length === own.length;
      return {
        kind: 'photos', ...common, height, columns,
        photos: perPhoto ? own.map((p, i) => ({ ...p, caption: caps[i] })) : own,
        caption: perPhoto ? null : caps.join(' ').trim() || null,
      };
    }
    default:
      return null;
  }
};

export const toInput = (s: EditorState): BrochureGenerateInput | null => {
  if (!s.addressLine.trim() || !s.city.trim() || !s.state.trim()) return null;
  const built: BrochureSectionInput[] = s.sections
    .filter((sec) => sec.title.trim())
    .map((sec) => ({
      title: sec.title.trim(),
      subtitle: sec.subtitle.trim() || null,
      sourceNote: sec.sourceNote.trim() || null,
      omitDisclaimer: sec.omitDisclaimer,
      columns: (sec.columns === '3' ? 3 : 2) as 2 | 3,
      omitFooter: sec.omitFooter,
      blocks: sec.blocks
        .map((b) => toBlock(
          b,
          sec.title.trim().toUpperCase() === 'SITE PLAN' && s.planPhoto
            ? [{ src: s.planPhoto, alt: 'Site plan' }]
            : s.gridPhotos
        ))
        .filter((b): b is BrochureBlockInput => b !== null)
        // A block with no title and no content is one he has not written yet, not an error.
        // (A stat row's title is optional, so an untitled one with figures still counts.)
        .filter((b) => ('title' in b && typeof b.title === 'string' ? b.title.trim().length > 0 : true)),
    }))
    .filter((sec) => sec.blocks.length > 0);

  const c = s.carried;
  const flyerPhotos = c.flyerPhotos ?? s.gridPhotos.slice(0, 2).map((p, i) => ({ ...p, caption: lines(s.flyerCaptions)[i] ?? null }));
  const picks: BrochureFlyerPicks = {};
  for (const slot of FLYER_SLOTS) {
    const p = s.flyerPicks[slot];
    if (p && p.section.trim()) picks[slot] = { section: p.section.trim(), block: p.block.trim() };
  }
  const hasPicks = Object.keys(picks).length > 0;

  return {
    property: {
      addressLine: s.addressLine.trim(), city: s.city.trim(), state: s.state.trim(),
      zip: s.zip.trim() || null, county: s.county.trim() || null,
    },
    dealKind: s.dealKind,
    format: s.format,
    theme: s.theme,
    ...(c.pageSize ? { pageSize: c.pageSize } : {}),
    // The flyer's back page takes the first two grid photographs, captioned one per line.
    flyer: s.format === 'flyer'
      ? {
          summary: s.flyerSummary.trim() || null,
          photos: flyerPhotos,
          ...(hasPicks ? { picks } : {}),
          ...(c.flyerTitle != null ? { title: c.flyerTitle } : {}),
          ...(c.flyerEyebrow != null ? { eyebrow: c.flyerEyebrow } : {}),
          ...(c.flyerEyebrowFront != null ? { eyebrowFront: c.flyerEyebrowFront } : {}),
        }
      : null,
    bandSet: s.bandSet,
    cover: {
      style: s.coverStyle,
      tagline: s.tagline.trim() || null,
      headline: s.headline.trim() || null,
      subhead: s.subhead.trim() || null,
      ...(s.emphasis.trim() ? { emphasis: s.emphasis.trim() } : {}),
      photo: s.coverPhoto ? { src: s.coverPhoto, ...(c.coverPhotoAlt != null ? { alt: c.coverPhotoAlt } : {}) } : null,
      stats: parseStats(s.coverStats),
      byline: s.byline.trim() || null,
      ...(c.coverLocality != null ? { locality: c.coverLocality } : {}),
    },
    sections: built,
    ...(c.financials ? { financials: c.financials } : {}),
    backCover: { band: s.backBand.trim() || null, photo: s.backPhoto ? { src: s.backPhoto, ...(c.backPhotoAlt != null ? { alt: c.backPhotoAlt } : {}) } : null, omit: s.omitBack },
    contact: {
      lead: s.contactLead || null, note: s.contactNote.trim() || null,
      ...(c.contactQrLead != null ? { qrLead: c.contactQrLead } : {}),
      ...(c.contactClosing != null ? { closing: c.contactClosing } : {}),
      ...(c.contactFooter != null ? { footer: c.contactFooter } : {}),
    },
    includeRobert: s.includeRobert, includeJoseph: s.includeJoseph,
    brokerTitles: s.brokerTitles,
    qrSrc: s.qrSrc,
    disclaimer: s.disclaimerText.trim() || null,
    pageFooter: s.pageFooter,
    footerTag: s.footerTag.trim() || null,
  };
};

// ------------------------------------------------------------------------------------------
// Input -> editor (LB4)
// ------------------------------------------------------------------------------------------

const photoLines = (block: Extract<BrochureBlockInput, { kind: 'photos' }>): string[] => {
  const out: string[] = [];
  if (block.height != null) out.push(`height: ${block.height}`);
  if (block.columns != null) out.push(`columns: ${block.columns}`);
  const perPhoto = block.photos.length > 0 && block.photos.every((p) => typeof p.caption === 'string' && p.caption.trim());
  if (perPhoto) out.push(...block.photos.map((p) => (p.caption as string).trim()));
  else if (block.caption?.trim()) out.push(block.caption.trim());
  return out;
};

const stripPhoto = (p: BrochurePhotoInput): { src: string; alt?: string } =>
  p.alt != null ? { src: p.src, alt: p.alt } : { src: p.src };

/**
 * One saved block back into the editor's text. `notes` collects what the text format cannot
 * hold exactly, so the screen can say it rather than the load hiding it.
 */
export const fromBlock = (
  block: BrochureBlockInput,
  grid: { src: string; alt?: string }[],
  notes: string[]
): EditorBlock => {
  const b = newBlock(block.kind);
  const span = 'span' in block ? block.span : undefined;
  const column = 'column' in block ? block.column : undefined;
  b.full = span === 'full';
  if (span === 'half') b.spanHalf = true;
  b.column = column === 1 ? '1' : column === 2 ? '2' : '';
  b.panel = 'panel' in block ? !!block.panel : false;
  b.title = 'title' in block && typeof block.title === 'string' ? block.title : '';

  switch (block.kind) {
    case 'facts': {
      const bare = block.rows.length > 0 && block.rows.every((r) => !/:$/.test(r.label.trim()));
      if (bare) b.bareLabels = true;
      b.body = block.rows.map((r) => `${bare ? `${r.label.trim()}:` : r.label.trim()} ${r.value}`.trim()).join('\n');
      if (block.lead != null) { b.carried = { lead: block.lead }; notes.push(`the lead line of "${block.title}"`); }
      break;
    }
    case 'bullets':
      b.body = block.items.join('\n');
      break;
    case 'prose':
      b.body = block.paragraphs.join('\n\n');
      break;
    case 'groups':
      b.body = [
        ...(block.columns ? [`columns: ${block.columns}`] : []),
        ...block.groups.map((g) => `${g.label.replace(/:$/, '')}: ${g.text}`),
      ].join('\n');
      break;
    case 'statRow':
      b.title = block.title ?? '';
      b.body = [...block.stats.map((s) => `${s.value} | ${s.label}`), ...(block.note ? [`note: ${block.note}`] : [])].join('\n');
      break;
    case 'callout':
      if (block.figure && block.figure.length > 24) notes.push(`the figure of "${block.title}" is longer than the editor's 24-character figure line and will print as body`);
      b.body = [...(block.figure ? [block.figure] : []), block.body].join('\n');
      break;
    case 'statBand':
      if (!block.label) notes.push('a stat band with no label (the editor needs one line for it)');
      b.body = [block.label ?? '-', block.figure, ...block.columns.map((c) => `${c.label.replace(/:$/, '')}: ${c.value}`)].join('\n');
      break;
    case 'statCards':
      b.body = statCardsToText(block);
      break;
    case 'table':
      b.body = [
        block.columns.join(' | '),
        ...block.rows.map((r) => r.join(' | ')),
        ...(block.totals ? [`= ${block.totals.join(' | ')}`] : []),
      ].join('\n');
      if (block.sourceNote != null) { b.carried = { sourceNote: block.sourceNote }; notes.push(`the source note under "${block.title}"`); }
      break;
    case 'photos': {
      const own = block.photos.map(stripPhoto);
      if (!samePhotos(own, grid)) b.photos = own;
      b.body = photoLines(block).join('\n');
      break;
    }
  }
  return b;
};

export interface LoadedEditorState {
  state: EditorState;
  /** What is carried but not editable here, in words - the screen prints it. */
  notes: string[];
}

export const fromInput = (input: BrochureGenerateInput): LoadedEditorState => {
  const notes: string[] = [];
  const sections = input.sections ?? [];
  const isPlan = (t: string) => t.trim().toUpperCase() === 'SITE PLAN';

  // The editor holds ONE grid of photographs. It is the first photo block outside the site plan;
  // every other block that differs keeps its own (carried), so nothing on his pages moves.
  let grid: { src: string; alt?: string }[] = [];
  let planPhoto: string | null = null;
  for (const sec of sections) {
    for (const blk of sec.blocks) {
      if (blk.kind !== 'photos') continue;
      if (isPlan(sec.title)) { if (!planPhoto && blk.photos[0]) planPhoto = blk.photos[0].src; continue; }
      if (!grid.length && blk.photos.length) grid = blk.photos.map(stripPhoto);
    }
  }
  if (!grid.length && input.flyer?.photos?.length) grid = input.flyer.photos.map(stripPhoto);

  const editorSections: EditorSection[] = sections.map((sec) => {
    const s = newSection(sec.title);
    s.subtitle = sec.subtitle ?? '';
    s.sourceNote = sec.sourceNote ?? '';
    s.omitDisclaimer = !!sec.omitDisclaimer;
    s.columns = sec.columns === 3 ? '3' : '2';
    s.omitFooter = !!sec.omitFooter;
    const planGrid = isPlan(sec.title) && planPhoto ? [{ src: planPhoto, alt: 'Site plan' }] : grid;
    s.blocks = sec.blocks.map((blk) => fromBlock(blk, planGrid, notes));
    return s;
  });
  const ownPhotoBlocks = editorSections.flatMap((s) => s.blocks).filter((b) => b.kind === 'photos' && b.photos).length;
  if (ownPhotoBlocks) notes.push(`${ownPhotoBlocks} photo block${ownPhotoBlocks === 1 ? '' : 's'} with their own photographs (the picker below edits the first grid only)`);

  const carried: CarriedInput = {};
  if (input.pageSize) { carried.pageSize = input.pageSize; notes.push(`the page size (${input.pageSize})`); }
  if (input.cover?.locality != null) { carried.coverLocality = input.cover.locality; notes.push('the cover locality line'); }
  if (input.cover?.photo?.alt != null) carried.coverPhotoAlt = input.cover.photo.alt;
  if (input.backCover?.photo?.alt != null) carried.backPhotoAlt = input.backCover.photo.alt;
  if (input.financials) { carried.financials = input.financials; notes.push('the financials (this is an Offering Memorandum)'); }
  if (input.contact?.qrLead != null) { carried.contactQrLead = input.contact.qrLead; notes.push('the contact page QR lead'); }
  if (input.contact?.closing != null) { carried.contactClosing = input.contact.closing; notes.push('the contact page closing line'); }
  if (input.contact?.footer != null) { carried.contactFooter = input.contact.footer; notes.push('the contact page footer line'); }
  if (input.flyer?.title != null) { carried.flyerTitle = input.flyer.title; notes.push('the flyer title'); }
  if (input.flyer?.eyebrow != null) { carried.flyerEyebrow = input.flyer.eyebrow; notes.push('the flyer eyebrow'); }
  if (input.flyer?.eyebrowFront != null) { carried.flyerEyebrowFront = input.flyer.eyebrowFront; notes.push('the flyer front eyebrow'); }

  const flyerPhotos = input.flyer?.photos ?? [];
  const flyerCaptions = flyerPhotos.map((p) => p.caption ?? '').join('\n');
  const flyerPicks: Partial<Record<FlyerSlot, BrochureFlyerPick>> = {};
  for (const slot of FLYER_SLOTS) {
    const p = input.flyer?.picks?.[slot];
    if (p && p.section) flyerPicks[slot] = { section: p.section, block: p.block ?? '' };
  }
  const flyerFromGrid = samePhotos(flyerPhotos, grid.slice(0, flyerPhotos.length)) && flyerPhotos.length <= 2;
  if (flyerPhotos.length && !flyerFromGrid) { carried.flyerPhotos = flyerPhotos; notes.push('the flyer\'s own two photographs'); }

  const state: EditorState = {
    addressLine: input.property.addressLine ?? '',
    city: input.property.city ?? '',
    state: input.property.state ?? 'CA',
    zip: input.property.zip ?? '',
    county: input.property.county ?? '',
    dealKind: input.dealKind === 'lease' ? 'lease' : 'sale',
    format: input.format === 'flyer' ? 'flyer' : 'brochure',
    theme: input.theme === 'manchester' || input.theme === 'classic' ? input.theme : 'apex',
    flyerSummary: input.flyer?.summary ?? '',
    flyerCaptions,
    flyerPicks,
    bandSet: input.bandSet === 'leasing' ? 'leasing' : 'house',
    coverStyle: input.cover?.style === 'overlay' || input.cover?.style === 'band' ? input.cover.style : 'card',
    tagline: input.cover?.tagline ?? '',
    headline: input.cover?.headline ?? '',
    subhead: input.cover?.subhead ?? '',
    emphasis: input.cover?.emphasis ?? '',
    coverStats: statsToText(input.cover?.stats),
    byline: input.cover?.byline ?? '',
    brokerTitles: !!input.brokerTitles,
    pageFooter: !!input.pageFooter,
    footerTag: input.footerTag ?? '',
    omitBack: !!input.backCover?.omit,
    contactLead: input.contact?.lead ?? '',
    contactNote: input.contact?.note ?? '',
    disclaimerText: input.disclaimer ?? '',
    backBand: input.backCover?.band ?? '',
    includeRobert: input.includeRobert !== false,
    includeJoseph: input.includeJoseph !== false,
    sections: editorSections,
    coverPhoto: input.cover?.photo?.src ?? null,
    backPhoto: input.backCover?.photo?.src ?? null,
    gridPhotos: grid,
    planPhoto,
    qrSrc: input.qrSrc ?? null,
    carried,
  };
  return { state, notes };
};
